import React, { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDroppable, useDraggable } from "@dnd-kit/core";

export type AddDocumentType = "blank" | "pdf" | "docx";

const TabBar: React.FC<{
  paneId: string;
  tabIds: string[];
  activeTabId?: string;
  onActivate: (tabId: string) => void;
  onSplit: (tabId: string) => void;
  getTabTitle?: (tabId: string) => string;
  getTabUnsaved?: (tabId: string) => boolean;
  onAddDocument?: (type: AddDocumentType) => void;
  onRenameTab?: (tabId: string, newName: string) => void;
  onRemoveTab?: (tabId: string) => void;
}> = ({
  paneId,
  tabIds,
  activeTabId,
  onActivate,
  onSplit,
  getTabTitle,
  getTabUnsaved,
  onAddDocument,
  onRenameTab,
  onRemoveTab,
}) => {
  const [contextTarget, setContextTarget] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addMenuPos, setAddMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [removeConfirmTarget, setRemoveConfirmTarget] = useState<string | null>(
    null
  );
  const addMenuRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // Close add menu when clicking outside
  useEffect(() => {
    if (!showAddMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      // Check if click is outside both menu and button
      const isOutsideMenu =
        addMenuRef.current && !addMenuRef.current.contains(e.target as Node);
      const isOutsideBtn =
        addBtnRef.current && !addBtnRef.current.contains(e.target as Node);

      if (isOutsideMenu && isOutsideBtn) {
        setShowAddMenu(false);
      }
    };

    // Add listener with a small delay to avoid catching the opening click
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showAddMenu]);

  // Close context menu when clicking outside
  useEffect(() => {
    if (!contextTarget) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(e.target as Node)
      ) {
        setContextTarget(null);
      }
    };

    // Add listener with a small delay to avoid catching the opening click
    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [contextTarget]);

  // make whole tablist droppable as end target
  const { setNodeRef: setBarDropRef } = useDroppable({
    id: `${paneId}::bar::end`,
  });

  const { t } = useTranslation();

  const handleRemoveDocumentClick = () => {
    if (removeConfirmTarget && onRemoveTab) {
      onRemoveTab(removeConfirmTarget);
      setRemoveConfirmTarget(null);
    }
  };

  return (
    <div className="patent-draft-agent-tabs-container">
      <ul className="patent-draft-agent-tabs nav nav-tabs" ref={setBarDropRef}>
        {tabIds.map((id, idx) => (
          <DraggableTab
            key={id}
            id={id}
            paneId={paneId}
            index={idx}
            isActive={id === activeTabId}
            hasUnsavedChanges={getTabUnsaved ? getTabUnsaved(id) : false}
            onClick={() => onActivate(id)}
            onContextMenu={e => {
              setContextTarget(id);
              setMenuPos({ x: e.clientX, y: e.clientY });
            }}
            getTitle={getTabTitle}
            onRename={onRenameTab}
            shouldStartRenaming={renamingTabId === id}
            onRenamingStarted={() => setRenamingTabId(null)}
          />
        ))}
        {/* Add document button */}
        {onAddDocument && (
          <li className="nav-item da-add-item">
            <button
              ref={addBtnRef}
              type="button"
              className="nav-link da-add-btn"
              title="Add document"
              onClick={() => {
                if (!showAddMenu && addBtnRef.current) {
                  const rect = addBtnRef.current.getBoundingClientRect();
                  setAddMenuPos({ top: rect.bottom + 4, left: rect.left });
                }
                setShowAddMenu(!showAddMenu);
              }}
            >
              +
            </button>
            {showAddMenu && addMenuPos && (
              <div
                ref={addMenuRef}
                className="da-add-menu"
                style={{ top: addMenuPos.top, left: addMenuPos.left }}
              >
                <button
                  type="button"
                  onClick={() => {
                    onAddDocument("blank");
                    setShowAddMenu(false);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0zM9.5 3.5v-2l3 3h-2A1 1 0 0 1 9.5 3.5zM4 1h5v2.5A1.5 1.5 0 0 0 10.5 5H13v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1z" />
                  </svg>
                  Blank Document
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onAddDocument("pdf");
                    setShowAddMenu(false);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
                    <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z" />
                  </svg>
                  PDF
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onAddDocument("docx");
                    setShowAddMenu(false);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
                  </svg>
                  Word
                </button>
              </div>
            )}
          </li>
        )}
      </ul>
      {contextTarget && (
        <div
          ref={contextMenuRef}
          className="da-tab-context"
          style={menuPos ? { top: menuPos.y, left: menuPos.x } : undefined}
        >
          {onRenameTab && (
            <button
              onClick={() => {
                setRenamingTabId(contextTarget);
                setContextTarget(null);
              }}
            >
              {t("documentAgent.renameTab", "Rename")}
            </button>
          )}
          <button
            onClick={() => {
              onSplit(contextTarget);
              setContextTarget(null);
            }}
          >
            {t("documentAgent.splitToNewWindow", "Split to new window")}
          </button>
          {onRemoveTab && (
            <button
              className="da-tab-context-danger"
              onClick={() => {
                setRemoveConfirmTarget(contextTarget);
                setContextTarget(null);
              }}
            >
              {t("documentAgent.removeDocument", "Remove document")}
            </button>
          )}
        </div>
      )}

      {/* Remove Document Confirmation Modal */}
      <Modal
        show={removeConfirmTarget !== null}
        onHide={() => setRemoveConfirmTarget(null)}
        centered
        size="sm"
        className="da-remove-document-modal"
      >
        <Modal.Body className="text-center py-4 px-4">
          <div className="delete-confirm-icon">🗑️</div>
          <h5 className="mb-2">
            {t("documentAgent.confirmRemoveDocument", "Remove document?")}
          </h5>
          {removeConfirmTarget && getTabTitle && (
            <div className="da-document-name mb-3">
              {getTabTitle(removeConfirmTarget)}
            </div>
          )}
          <p className="text-muted mb-0">
            {t(
              "documentAgent.removeDocumentWarning",
              "This action cannot be undone."
            )}
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center border-0 pt-0 pb-4">
          <Button
            variant="outline-secondary"
            onClick={() => setRemoveConfirmTarget(null)}
          >
            {t("documentAgentChat.cancel", "Cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleRemoveDocumentClick}
          >
            {t("documentAgent.removeDocument", "Remove")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const DraggableTab: React.FC<{
  id: string;
  paneId: string;
  index: number;
  isActive: boolean;
  hasUnsavedChanges?: boolean;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  getTitle?: (tabId: string) => string;
  onRename?: (tabId: string, newName: string) => void;
  shouldStartRenaming?: boolean;
  onRenamingStarted?: () => void;
}> = ({
  id,
  paneId,
  index,
  isActive,
  hasUnsavedChanges,
  onClick,
  onContextMenu,
  getTitle,
  onRename,
  shouldStartRenaming,
  onRenamingStarted,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `${paneId}::${id}`,
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `${paneId}::bar::${index}`,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const style: React.CSSProperties | undefined = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  // Start editing when triggered from context menu
  useEffect(() => {
    if (shouldStartRenaming && onRename) {
      const currentTitle = getTitle ? getTitle(id) : id;
      setEditValue(currentTitle);
      setIsEditing(true);
      onRenamingStarted?.();
    }
  }, [shouldStartRenaming, onRename, getTitle, id, onRenamingStarted]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onRename) return;
    const currentTitle = getTitle ? getTitle(id) : id;
    setEditValue(currentTitle);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && onRename) {
      onRename(id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <li ref={setDropRef} className="nav-item">
        <input
          ref={inputRef}
          type="text"
          className="nav-link da-tab-rename-input"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          style={{ minWidth: 80 }}
        />
      </li>
    );
  }

  return (
    <li ref={setDropRef} className="nav-item">
      <button
        ref={setNodeRef}
        className={`nav-link ${isActive ? "active" : ""} ${hasUnsavedChanges ? "has-unsaved" : ""}`}
        style={style}
        title={getTitle ? getTitle(id) : id}
        onClick={onClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={e => {
          e.preventDefault();
          onContextMenu(e);
        }}
        {...listeners}
        {...filterDraggableAttributes(attributes)}
      >
        {getTitle ? getTitle(id) : id}
        {hasUnsavedChanges && <span className="tab-unsaved-dot" />}
      </button>
    </li>
  );
};

function filterDraggableAttributes(attrs: any) {
  if (!attrs) return {} as any;
  const { role, "aria-pressed": _ariaPressed, ...rest } = attrs as any;
  return rest;
}

export default TabBar;
